import React, { ReactElement, ComponentPropsWithoutRef, useRef } from "react";
import _ from "lodash";
import classnames from "classnames";

import { InputLabel } from "../input/InputLabel";
import { InputError } from "../input/InputError";
import { randomId } from "../helpers";

export interface TextareaProps extends ComponentPropsWithoutRef<"textarea"> {
  value: string;
  label?: string | ReactElement;
  error?: string[] | ReactElement[] | string | ReactElement;
  hint?: ReactElement;
  showLimit?: boolean;
}

export const Textarea = ({
  id,
  value,
  label,
  hint,
  error,
  maxLength,
  showLimit,
  disabled,
  readOnly,
  className,
  ...textareaAttr
}: TextareaProps) => {
  const errorDescribedById = useRef(`error${randomId()}`);
  const hasLabel = label || showLimit;
  const limitExceeded = showLimit && value.trim().length > maxLength;
  const hasErrorMessage = !_.isEmpty(error);

  const hasError = limitExceeded || hasErrorMessage;

  const textareaWrapperClass = classnames("textarea", className, {
    "textarea--disabled": disabled,
    "textarea--readonly": readOnly,
    "textarea--has-label": hasLabel,
    "textarea--has-hint": hint,
    "textarea--has-error": hasError,
  });

  return (
    <div className={textareaWrapperClass}>
      {hasLabel && (
        <InputLabel
          id={id}
          label={label}
          value={value}
          showLimit={showLimit}
          maxLength={maxLength}
        />
      )}

      <textarea
        className={"textarea-control"}
        id={id}
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        readOnly={readOnly}
        {...(hasError && {
          "aria-invalid": true,
          "aria-describedby": errorDescribedById.current,
        })}
        {...textareaAttr}
      />

      {hint && <div className="textarea__hint">{hint}</div>}

      {hasErrorMessage && (
        <InputError
          error={error}
          ariaDescribedById={errorDescribedById.current}
        />
      )}
    </div>
  );
};
